import { gql, useQuery } from '@apollo/client';
import { Box } from '@mui/material';
import { useState } from 'react';
import { LoadingErrorWrapper } from '@/admins/SmartReports/components/Components/Common/LoadingErrorWrapper';
import type {
  ComponentDefinition,
  ComponentEditProps,
  ComponentViewProps,
  UseDataType,
} from '@/admins/SmartReports/components/Components/types';
import { SmartReportComponentTypesEnum, SmartReportTypeEnum } from '@/admins/SmartReports/types';

export const COMPONENT_ID = 'asset_name';

// we need the ID for Apollo to cache properly
const GET_ASSET_QUERY = gql`
  query assetV2($input: EntityIdentifierInput!) {
    assetV2(input: $input) {
      id
      displayName
    }
  }
`;

const AssetNameView = ({ data, sx }: ComponentViewProps<string>) => {
  return <Box sx={{ ...sx }}>{data || 'Asset Name'}</Box>;
};

const useData = ({ portfolio, persistedData, onComponentLoad }: UseDataType<string>) => {
  const [dataFetched, setDataFetched] = useState(persistedData);
  const { loading, error } = useQuery(GET_ASSET_QUERY, {
    variables: { input: { id: portfolio?.assets?.[0] } },
    skip: !!persistedData,
    onCompleted: data => {
      if (data?.assetV2?.displayName) {
        setDataFetched(data?.assetV2?.displayName);
        if (onComponentLoad) {
          onComponentLoad({
            componentId: COMPONENT_ID,
            data: data?.assetV2?.displayName,
          });
        }
      }
    },
  });

  return { data: dataFetched, error, loading };
};

export const AssetName = ({
  portfolio,
  persistedData,
  onComponentLoad,
  sx,
}: ComponentEditProps<string>) => {
  const { data, loading, error } = useData({
    portfolio,
    persistedData,
    onComponentLoad,
  });

  return (
    <LoadingErrorWrapper loading={loading} error={error} data={data}>
      {data && <AssetNameView sx={sx} data={data} />}
    </LoadingErrorWrapper>
  );
};

export const AssetNameComponent: ComponentDefinition<string> = {
  id: COMPONENT_ID,
  name: 'Asset Name',
  type: SmartReportComponentTypesEnum.TextField,
  reportTypes: [SmartReportTypeEnum.Asset],
  Thumbnail: AssetName,
  Component: AssetName,
  ComponentView: AssetNameView,
};
